import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as SignalR from '@microsoft/signalr';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import configData from '../../../config';
import DailyStreakTrackerRing from '../../PortalV2/Steps/DailyStreakTrackerRing';
import ActionStepMenu from '../ActionStepMenu';
import { useCountUp } from '../useCountUp';
import './ActionTrackerWorkspace.css';

const TABS = [
    { id: 'tracker', label: 'Tracker' },
    { id: 'daily', label: 'Daily Actions' },
    { id: 'todo', label: 'To-Do List' },
];

const MAX_DAILY = 15;
const DISPLAY_SEGMENTS = 3;

function mapNamedCompletedToday(list) {
    return (Array.isArray(list) ? list : [])
        .filter((a) => a && String(a.text || a.Text || '').trim())
        .map((a) => ({
            id: a.id ?? a.Id,
            text: a.text ?? a.Text,
            extraText: a.extraText ?? a.ExtraText,
            canBeRemoved:
                a.canBeRemoved !== false && a.CanBeRemoved !== false,
        }));
}

function pickNamedCompletedToday(data) {
    if (!data || typeof data !== 'object') return null;
    if (Array.isArray(data.namedCompletedToday)) return data.namedCompletedToday;
    if (Array.isArray(data.NamedCompletedToday)) return data.NamedCompletedToday;
    if (Array.isArray(data.todayCompletedActions)) return data.todayCompletedActions;
    if (Array.isArray(data.TodayCompletedActions)) return data.TodayCompletedActions;
    return null;
}

function numberWithCommas(x) {
    if (x == null || x === '') return '0';
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function dayLabel(offset) {
    if (offset === 0) return 'Today';
    if (offset === 1) return 'Tomorrow';
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d.toLocaleDateString('en-US', { weekday: 'long' });
}

function ConfirmModal({ title, desc, confirmLabel, onCancel, onConfirm, busy }) {
    return (
        <div className="dv2-at__modal" role="dialog" aria-modal="true">
            <div className="dv2-at__modal-card">
                <h4>{title}</h4>
                <p>{desc}</p>
                <div className="dv2-at__modal-actions">
                    <button
                        type="button"
                        className="dv2-at__btn-ghost"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="dv2-at__btn-primary"
                        onClick={onConfirm}
                        disabled={busy}
                    >
                        {confirmLabel || 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TextModal({
    title,
    placeholder,
    initialValue = '',
    hint = '',
    onCancel,
    onSave,
    busy,
    wide = false,
}) {
    const [value, setValue] = useState(initialValue);
    const submit = () => {
        if (busy) return;
        const trimmed = String(value || '').trim();
        if (!trimmed) return;
        onSave(value);
    };
    return (
        <div className="dv2-at__modal" role="dialog" aria-modal="true">
            <div
                className={`dv2-at__modal-card${
                    wide ? ' dv2-at__modal-card--wide' : ''
                }`}
            >
                <h4>{title}</h4>
                {hint ? <p className="dv2-at__modal-hint">{hint}</p> : null}
                <textarea
                    className="dv2-at__input dv2-at__input--area"
                    value={value}
                    placeholder={placeholder}
                    autoFocus
                    rows={wide ? 4 : 3}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                />
                <div className="dv2-at__modal-actions">
                    <button
                        type="button"
                        className="dv2-at__btn-ghost"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="dv2-at__btn-primary"
                        onClick={submit}
                        disabled={busy || !String(value || '').trim()}
                    >
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Full Action Tracker workspace for Dashboard V2.
 * Tabs: Tracker · Daily Actions · To-Do List
 */
const ActionTrackerWorkspace = ({
    initialTab = 'tracker',
    isLimited = false,
    onBack,
    onProgressRefresh,
}) => {
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession() || {};
    const { token, userId, userTypeId } = session;

    const [tab, setTab] = useState(initialTab);
    const [dayOffset, setDayOffset] = useState(0);
    const [actions, setActions] = useState([]);
    const [todayCount, setTodayCount] = useState(0);
    const [lifetimeSteps, setLifetimeSteps] = useState(0);
    const [globalSteps, setGlobalSteps] = useState(0);
    const [todoDays, setTodoDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dayFetching, setDayFetching] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [listHint, setListHint] = useState(false);
    const [pickBuzz, setPickBuzz] = useState(false);
    const [completedToday, setCompletedToday] = useState([]);

    const [confirm, setConfirm] = useState(null);
    const [textModal, setTextModal] = useState(null);
    const [menuActionId, setMenuActionId] = useState(null);
    const menuRef = useRef(null);
    const pickBuzzTimerRef = useRef(null);
    const trackerListWrapRef = useRef(null);

    const todoLocked = isLimited || Number(userTypeId) === 4;
    const actionsCacheRef = useRef({});
    const actionsReqRef = useRef(0);
    const dayOffsetRef = useRef(dayOffset);
    dayOffsetRef.current = dayOffset;

    const applyActions = useCallback((offset, next, todayActionsCount, todayCompletedActions) => {
        const list = Array.isArray(next) ? next : [];
        actionsCacheRef.current[offset] = list;
        if (offset === dayOffsetRef.current) {
            setActions(list);
        }
        if (todayActionsCount != null) {
            setTodayCount(Number(todayActionsCount) || 0);
        }
        if (offset === 0) {
            const completedRaw = Array.isArray(todayCompletedActions)
                ? todayCompletedActions
                : null;
            if (completedRaw) {
                const mapped = mapNamedCompletedToday(completedRaw);
                setCompletedToday((prev) => {
                    if (mapped.length > 0) return mapped;
                    return prev.filter(
                        (c) => !list.some((a) => a.id === c.id)
                    );
                });
            } else {
                setCompletedToday((prev) =>
                    prev.filter((c) => !list.some((a) => a.id === c.id))
                );
            }
        }
    }, []);

    const invalidateActionsCache = useCallback((keepOffset = null) => {
        if (keepOffset == null) {
            actionsCacheRef.current = {};
            return;
        }
        const kept = actionsCacheRef.current[keepOffset];
        actionsCacheRef.current = kept ? { [keepOffset]: kept } : {};
    }, []);

    const loadActions = useCallback(
        async (offset = dayOffsetRef.current, { soft = false } = {}) => {
            if (!token || !userId) return;

            const cached = actionsCacheRef.current[offset];
            if (offset === dayOffsetRef.current) {
                if (cached) setActions(cached);
                else if (soft) setActions([]);
            }

            const reqId = ++actionsReqRef.current;
            if (!soft && !cached) setLoading(true);
            else if (!cached) setDayFetching(true);

            setErrorMsg('');
            const { error, data } = await requestAxios({
                url: `Actions/GetUserNotCompletedActions/${userId}/${offset}`,
                token,
            });

            if (reqId !== actionsReqRef.current) return;

            if (!error && data) {
                applyActions(
                    offset,
                    data.actions,
                    data.todayActionsCount,
                    pickNamedCompletedToday(data)
                );
            } else if (!cached) {
                setErrorMsg('Could not load actions.');
            }

            setLoading(false);
            setDayFetching(false);
        },
        [token, userId, applyActions]
    );

    const prefetchActions = useCallback(
        async (offset) => {
            if (!token || !userId) return;
            if (offset < 0 || offset > 6) return;
            if (actionsCacheRef.current[offset]) return;
            const { error, data } = await requestAxios({
                url: `Actions/GetUserNotCompletedActions/${userId}/${offset}`,
                token,
            });
            if (!error && data) {
                actionsCacheRef.current[offset] = Array.isArray(data.actions)
                    ? data.actions
                    : [];
                if (data.todayActionsCount != null) {
                    setTodayCount(Number(data.todayActionsCount) || 0);
                }
            }
        },
        [token, userId]
    );

    const loadLifetime = useCallback(async () => {
        if (!token || !userId) return;
        const { error, data } = await requestAxios({
            url: `Dashboard/GetUserProgress/${userId}`,
            token,
        });
        if (!error && data) {
            setLifetimeSteps(Number(data.stepsTaken) || 0);
        }
    }, [token, userId]);

    const loadGlobal = useCallback(async () => {
        if (!token) return;
        const { error, data } = await requestAxios({
            url: 'Actions/GetGlobalActionStepsTaken',
            token,
        });
        if (!error && data != null) setGlobalSteps(data);
    }, [token]);

    const loadTodos = useCallback(async () => {
        if (!token) return;
        const { error, data } = await requestAxios({
            url: 'ToDoList/GetTasks',
            token,
        });
        if (!error && Array.isArray(data)) setTodoDays(data);
    }, [token]);

    useEffect(() => {
        setTab(initialTab);
    }, [initialTab]);

    useEffect(() => {
        if (tab === 'tracker' && dayOffset !== 0) {
            setDayOffset(0);
        }
    }, [tab, dayOffset]);

    useEffect(() => {
        if (menuActionId == null) return undefined;
        const onPointerDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuActionId(null);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [menuActionId]);

    useEffect(
        () => () => {
            if (pickBuzzTimerRef.current) clearTimeout(pickBuzzTimerRef.current);
        },
        []
    );

    // Bootstrap once: meta only (actions load with day navigation)
    useEffect(() => {
        loadLifetime();
        loadGlobal();
        loadTodos();
    }, [loadLifetime, loadGlobal, loadTodos]);

    // Day navigation: actions only (To-Do already has all days client-side)
    useEffect(() => {
        if (tab === 'todo') return undefined;
        const hasCache = Boolean(actionsCacheRef.current[dayOffset]);
        const soft = hasCache || Object.keys(actionsCacheRef.current).length > 0;
        loadActions(dayOffset, { soft });
        const neighbors = [dayOffset - 1, dayOffset + 1];
        const t = window.setTimeout(() => {
            neighbors.forEach((o) => prefetchActions(o));
        }, 80);
        return () => window.clearTimeout(t);
    }, [dayOffset, tab, loadActions, prefetchActions]);

    useEffect(() => {
        if (!token) return undefined;
        const hub = new SignalR.HubConnectionBuilder()
            .withAutomaticReconnect()
            .withUrl(`${configData.SERVER_URL}actionhub`)
            .build();
        hub.start().catch(() => {});
        hub.on('GetGlobalStepsTaken', (steps) => setGlobalSteps(steps));
        return () => {
            hub.stop().catch(() => {});
        };
    }, [token]);

    const namedPending = useMemo(
        () => actions.filter((a) => a && String(a.text || '').trim()),
        [actions]
    );

    const trackerListRows = useMemo(() => {
        const pending = namedPending.map((a) => ({ ...a, done: false }));
        const done = completedToday
            .filter((c) => !pending.some((p) => p.id === c.id))
            .map((a) => ({ ...a, done: true }));
        return [...done, ...pending];
    }, [namedPending, completedToday]);

    const currentTodoDay = useMemo(() => {
        if (!todoDays.length) return null;
        const byNumber = todoDays.find(
            (d) => Number(d.dayNumber) === dayOffset
        );
        if (byNumber) return byNumber;
        return todoDays[dayOffset] || null;
    }, [todoDays, dayOffset]);

    const currentTodoTasks = currentTodoDay?.tasks || [];

    const refreshAfterComplete = async (justCompleted) => {
        if (justCompleted && dayOffset === 0) {
            setCompletedToday((prev) => {
                if (prev.some((c) => c.id === justCompleted.id)) return prev;
                return [...prev, justCompleted];
            });
            setActions((prev) => {
                const next = prev.filter((a) => a.id !== justCompleted.id);
                actionsCacheRef.current[0] = next;
                return next;
            });
        }
        invalidateActionsCache(dayOffset);
        await loadActions(dayOffset, { soft: true });
        await loadLifetime();
        if (onProgressRefresh) onProgressRefresh();
    };

    const runComplete = async (actionId) => {
        if (busy || todayCount >= MAX_DAILY) return;
        const action = actions.find((a) => a.id === actionId);
        setBusy(true);
        const { error } = await requestAxios({
            url: 'Actions/AsyncCompleteAction',
            method: 'put',
            token,
            data: { userId, id: actionId },
        });
        setBusy(false);
        setConfirm(null);
        setListHint(false);
        if (error) {
            setErrorMsg('Could not complete this action.');
            return;
        }
        await refreshAfterComplete(
            action
                ? {
                      id: action.id,
                      text: action.text,
                      extraText: action.extraText,
                  }
                : null
        );
    };

    const runEmptyComplete = async () => {
        if (busy || todayCount >= MAX_DAILY) return;
        setBusy(true);
        const { error } = await requestAxios({
            url: 'Actions/AsyncInsertEmptyCompletedAction',
            method: 'post',
            token,
            data: { userId, text: '' },
        });
        setBusy(false);
        setConfirm(null);
        if (error) {
            setErrorMsg('Could not record an action step.');
            return;
        }
        await refreshAfterComplete(null);
    };

    const handlePush = () => {
        if (busy || todayCount >= MAX_DAILY) return;
        if (namedPending.length === 0) {
            setListHint(false);
            runEmptyComplete();
            return;
        }
        setListHint(true);
        setPickBuzz(false);
        window.requestAnimationFrame(() => {
            setPickBuzz(true);
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
                navigator.vibrate([18, 32, 18]);
            }
        });
        if (pickBuzzTimerRef.current) clearTimeout(pickBuzzTimerRef.current);
        pickBuzzTimerRef.current = setTimeout(() => setPickBuzz(false), 720);
        if (trackerListWrapRef.current?.scrollIntoView) {
            trackerListWrapRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    const requestComplete = (action) => {
        if (busy || todayCount >= MAX_DAILY || action.canBeCompleted === false)
            return;
        setListHint(false);
        setConfirm({
            type: 'complete',
            id: action.id,
            title: 'Are you sure?',
            desc: 'Do you really want to complete this action?',
        });
    };

    const deleteAction = async (id) => {
        setBusy(true);
        const { error, data } = await requestAxios({
            url: `Actions/${id}`,
            method: 'delete',
            token,
        });
        setBusy(false);
        setConfirm(null);
        setMenuActionId(null);
        if (error) {
            setErrorMsg('Could not delete action.');
            return;
        }
        setCompletedToday((prev) => prev.filter((c) => c.id !== id));
        if (data) {
            applyActions(
                dayOffset,
                data.actions,
                data.todayCompletedActions ?? todayCount
            );
            invalidateActionsCache(dayOffset);
        } else {
            invalidateActionsCache();
            await loadActions(dayOffset, { soft: true });
        }
    };

    const saveActionText = async (text, existing = null, offsetOverride = null) => {
        const trimmed = String(text || '').trim();
        if (!trimmed || busy) return;

        const offset =
            offsetOverride != null ? offsetOverride : dayOffsetRef.current;
        const unlocked = new Date();
        unlocked.setDate(unlocked.getDate() + offset);
        unlocked.setHours(0, 0, 0, 0);
        const unlockedIso = unlocked.toISOString();

        // Close immediately — don't wait on the network
        setTextModal(null);
        setErrorMsg('');

        const isEdit = existing?.id != null && existing.id !== 0;
        const tempId = isEdit ? existing.id : `temp-${Date.now()}`;
        const snapshot = actions;

        if (isEdit) {
            setActions((prev) => {
                const next = prev.map((a) =>
                    a.id === existing.id ? { ...a, text: trimmed } : a
                );
                actionsCacheRef.current[offset] = next;
                return next;
            });
            setCompletedToday((prev) =>
                prev.map((a) =>
                    a.id === existing.id ? { ...a, text: trimmed } : a
                )
            );
        } else {
            const optimistic = {
                id: tempId,
                text: trimmed,
                unlockedDate: unlockedIso,
                canBeCompleted: true,
            };
            setActions((prev) => {
                const next = [...prev, optimistic];
                actionsCacheRef.current[offset] = next;
                return next;
            });
        }

        setBusy(true);
        const { error, data } = await requestAxios({
            url: 'Actions/UpdateInsertAction',
            method: 'post',
            token,
            data: {
                Id: existing?.id || 0,
                Text: trimmed,
                UnlockedDate: unlockedIso,
            },
        });
        setBusy(false);

        if (error) {
            setActions(snapshot);
            actionsCacheRef.current[offset] = snapshot;
            setErrorMsg('Could not save action.');
            return;
        }

        if (data?.actions) {
            applyActions(offset, data.actions, data.todayActionsCount);
            invalidateActionsCache(offset);
        } else {
            invalidateActionsCache();
            await loadActions(offset, { soft: true });
        }
    };

    const addTodo = async (text, dayNumber) => {
        const trimmed = String(text || '').trim();
        if (!trimmed) return;
        setBusy(true);
        const { error, data } = await requestAxios({
            url: 'ToDoList/AddTask',
            method: 'post',
            token,
            data: { text: trimmed, dayNumber },
        });
        setBusy(false);
        setTextModal(null);
        if (error) {
            setErrorMsg('Could not add task.');
            return;
        }
        if (Array.isArray(data)) setTodoDays(data);
        else await loadTodos();
    };

    const completeTodo = async (id) => {
        setBusy(true);
        const { error, data } = await requestAxios({
            url: `ToDoList/CompleteTask/${id}`,
            method: 'put',
            token,
        });
        setBusy(false);
        setConfirm(null);
        if (error) {
            setErrorMsg('Could not complete task.');
            return;
        }
        if (Array.isArray(data)) setTodoDays(data);
        else await loadTodos();
    };

    const deleteTodo = async (id) => {
        setBusy(true);
        const { error, data } = await requestAxios({
            url: `ToDoList/DeleteTask/${id}`,
            method: 'delete',
            token,
        });
        setBusy(false);
        setConfirm(null);
        if (error) {
            setErrorMsg('Could not delete task.');
            return;
        }
        if (Array.isArray(data)) setTodoDays(data);
        else await loadTodos();
    };

    const editTodo = async (text, task) => {
        const trimmed = String(text || '').trim();
        if (!trimmed || !task?.id || busy) return;
        setTextModal(null);
        setErrorMsg('');
        setBusy(true);
        const { error, data } = await requestAxios({
            url: 'ToDoList/EditTask',
            method: 'put',
            token,
            data: {
                Id: task.id,
                Text: trimmed,
            },
        });
        setBusy(false);
        if (error) {
            setErrorMsg('Could not save task.');
            return;
        }
        if (Array.isArray(data)) setTodoDays(data);
        else await loadTodos();
    };

    const requestCompleteTodo = (task) => {
        if (busy || !task?.id) return;
        setConfirm({
            type: 'complete-todo',
            id: task.id,
            title: 'Are you sure?',
            desc: 'Do you really want to complete this task?',
        });
    };

    const setTabAndUrl = (next) => {
        setTab(next);
        window.history.replaceState(
            null,
            '',
            `/dashboard-v2?tracker=1&tab=${next}`
        );
    };

    const streakDone = Math.min(todayCount, DISPLAY_SEGMENTS);
    const streakEarned = streakDone >= DISPLAY_SEGMENTS;
    const atDailyLimit = todayCount >= MAX_DAILY;
    const trackerDisabled = busy || atDailyLimit;
    const canPulse = !loading && !trackerDisabled && todayCount < MAX_DAILY;
    const displayGlobalSteps = useCountUp(globalSteps, {
        ready: !loading,
        duration: 1800,
    });

    let trackerPulseClass = ' trackerButton--complete';
    if (canPulse && todayCount < DISPLAY_SEGMENTS) {
        trackerPulseClass = ' trackerButton--pulseStreak';
    } else if (canPulse && todayCount < MAX_DAILY) {
        trackerPulseClass = ' trackerButton--pulseExtra';
    } else if (atDailyLimit) {
        trackerPulseClass = ' trackerButton--limitReached';
    }

    let listLead = '';
    if (todayCount <= 0) {
        listLead = 'Tap the tracker to complete your first action.';
    } else if (todayCount === 1) {
        listLead = "Nice start. Two more actions to earn today's streak.";
    } else if (todayCount === 2) {
        listLead = 'Keep going. One more action completes your streak.';
    } else if (todayCount === 3) {
        listLead =
            "Today's streak earned. You can keep going with extra actions.";
    } else if (todayCount >= 4 && todayCount < MAX_DAILY) {
        listLead = `Great momentum. You can complete up to ${MAX_DAILY} actions today.`;
    } else {
        listLead = "Great work. You reached today's action limit.";
    }

    const openAddTodayAction = () => {
        setDayOffset(0);
        setErrorMsg('');
        setTextModal({
            type: 'add-action',
            title: 'Add action',
            placeholder: 'What will you do today?',
            hint: 'For today',
            dayOffset: 0,
        });
    };

    const trackerStateClass = streakEarned
        ? ' is-streak-done'
        : streakDone > 0
          ? ' is-streak-partial'
          : ' is-streak-empty';

    return (
        <section className="dv2-at dv2-glass dv2-el--strong">
            <div className="dv2-at__top">
                <button type="button" className="dv2-at__back" onClick={onBack}>
                    ← Back to Dashboard
                </button>
            </div>

            <header className="dv2-at__header">
                <div className="dv2-at__header-left">
                    <div className="dv2-at__badge" aria-hidden>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                        >
                            <circle cx="12" cy="12" r="8" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="dv2-at__title">Action Tracker</h1>
                        <p className="dv2-at__subtitle">
                            Small steps. Daily momentum. Real change.
                        </p>
                    </div>
                </div>
                <div className="dv2-at__stat-pill">
                    <span className="dv2-at__stat-dot" />
                    {todayCount}/{MAX_DAILY} today
                </div>
            </header>

            <nav className="dv2-at__tabs" aria-label="Action Tracker sections">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`dv2-at__tab${
                            tab === t.id ? ' is-active' : ''
                        }`}
                        onClick={() => {
                            if (t.id === 'todo' && todoLocked) return;
                            setTabAndUrl(t.id);
                        }}
                        disabled={t.id === 'todo' && todoLocked}
                        title={
                            t.id === 'todo' && todoLocked
                                ? 'Available with an active subscription'
                                : undefined
                        }
                    >
                        {t.label}
                    </button>
                ))}
            </nav>

            {errorMsg ? <p className="dv2-at__error">{errorMsg}</p> : null}

            {tab === 'tracker' ? (
                <div className={`dv2-at__tracker${trackerStateClass}`}>
                    <div className="dv2-at__tracker-main">
                        <div className="dv2-at__tracker-left">
                            <div
                                className={`dv2-at__ring-wrap${trackerPulseClass}`}
                                title={
                                    atDailyLimit
                                        ? "Today's action limit reached"
                                        : 'Mark an action step complete'
                                }
                            >
                                <DailyStreakTrackerRing
                                    size="compact"
                                    variant="dashboard"
                                    accent="oola"
                                    totalActions={DISPLAY_SEGMENTS}
                                    completedActions={streakDone}
                                    label={String(todayCount)}
                                    subLabel="Actions"
                                    onTap={handlePush}
                                    disabled={trackerDisabled}
                                    keepInteractive
                                    pulseKey={todayCount}
                                    showAmbient={false}
                                    showGlow={false}
                                    showLogo={false}
                                />
                            </div>
                            <div className="dv2-at__global">
                                <span className="dv2-at__global-label">
                                    Global Momentum
                                </span>
                                <strong className="dv2-at__global-value">
                                    {numberWithCommas(displayGlobalSteps)}
                                </strong>
                                <span className="dv2-at__global-sub">
                                    actions completed
                                </span>
                            </div>
                        </div>

                        <div
                            ref={trackerListWrapRef}
                            className={`dv2-at__tracker-list-wrap${
                                pickBuzz ? ' is-pick-buzz' : ''
                            }${listHint ? ' is-pick-hint' : ''}`}
                        >
                            <div className="dv2-at__tracker-list-head">
                                <div className="dv2-at__tracker-list-head-copy">
                                    <h2>Today&apos;s Actions</h2>
                                    {listLead ? (
                                        <p
                                            key={todayCount}
                                            className="dv2-at__list-lead"
                                            role="status"
                                            aria-live="polite"
                                        >
                                            {listLead}
                                        </p>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    className="dv2-at__link"
                                    onClick={() => setTabAndUrl('daily')}
                                >
                                    Manage all →
                                </button>
                            </div>

                            {listHint && namedPending.length > 0 ? (
                                <p
                                    className={`dv2-at__hint${
                                        pickBuzz ? ' is-buzz' : ''
                                    }`}
                                >
                                    Select an action below to mark it complete.
                                </p>
                            ) : null}

                            {loading ? (
                                <p className="dv2-at__muted">Loading actions…</p>
                            ) : null}

                            {!loading && trackerListRows.length === 0 ? (
                                <div className="dv2-at__tracker-empty">
                                    <p className="dv2-at__tracker-empty-title">
                                        {streakEarned
                                            ? 'Your daily ritual is complete'
                                            : 'Ready when you are'}
                                    </p>
                                    <p>
                                        No more named actions for today. Add a
                                        new action or tap the tracker to keep
                                        building momentum.
                                    </p>
                                    <button
                                        type="button"
                                        className="dv2-at__add-cta"
                                        onClick={openAddTodayAction}
                                        disabled={busy}
                                    >
                                        + Add more
                                    </button>
                                </div>
                            ) : null}

                            {!loading && trackerListRows.length > 0 ? (
                                <>
                                    <div className="dv2-at__tracker-list-scroll">
                                        <ul className="dv2-at__tracker-list">
                                            {trackerListRows.map(
                                                (action, index) => {
                                                    const canClick =
                                                        !action.done &&
                                                        !busy &&
                                                        todayCount <
                                                            MAX_DAILY &&
                                                        action.canBeCompleted !==
                                                            false;
                                                    const canComplete =
                                                        canClick;
                                                    const canRemove =
                                                        action.canBeRemoved !==
                                                        false;
                                                    const menuOpen =
                                                        menuActionId ===
                                                        action.id;
                                                    const menuOpensUp =
                                                        index >=
                                                        trackerListRows.length -
                                                            2;
                                                    return (
                                                        <li
                                                            key={action.id}
                                                            className={[
                                                                'dv2-at__tracker-item',
                                                                action.done
                                                                    ? 'is-done'
                                                                    : 'is-pending',
                                                                canClick
                                                                    ? 'is-clickable'
                                                                    : '',
                                                                !action.done &&
                                                                pickBuzz
                                                                    ? 'is-pick-buzz'
                                                                    : '',
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ')}
                                                            onClick={
                                                                canClick
                                                                    ? () =>
                                                                          requestComplete(
                                                                              action
                                                                          )
                                                                    : undefined
                                                            }
                                                            onKeyDown={
                                                                canClick
                                                                    ? (e) => {
                                                                          if (
                                                                              e.key ===
                                                                                  'Enter' ||
                                                                              e.key ===
                                                                                  ' '
                                                                          ) {
                                                                              e.preventDefault();
                                                                              requestComplete(
                                                                                  action
                                                                              );
                                                                          }
                                                                      }
                                                                    : undefined
                                                            }
                                                            role={
                                                                canClick
                                                                    ? 'button'
                                                                    : undefined
                                                            }
                                                            tabIndex={
                                                                canClick
                                                                    ? 0
                                                                    : undefined
                                                            }
                                                            aria-label={
                                                                canClick
                                                                    ? `Complete: ${action.text}`
                                                                    : undefined
                                                            }
                                                        >
                                                            {action.done ? (
                                                                <span
                                                                    className="dv2-at__tracker-check is-done"
                                                                    aria-hidden
                                                                >
                                                                    ✓
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className={`dv2-at__tracker-check${
                                                                        !canClick
                                                                            ? ' is-disabled'
                                                                            : ''
                                                                    }`}
                                                                    aria-hidden
                                                                />
                                                            )}
                                                            <span className="dv2-at__tracker-text">
                                                                {action.text ||
                                                                    'Action step'}
                                                                {action.extraText ? (
                                                                    <em>
                                                                        {' '}
                                                                        {
                                                                            action.extraText
                                                                        }
                                                                    </em>
                                                                ) : null}
                                                            </span>
                                                            <ActionStepMenu
                                                                open={menuOpen}
                                                                opensUp={
                                                                    menuOpensUp
                                                                }
                                                                menuRef={
                                                                    menuRef
                                                                }
                                                                label={
                                                                    action.text ||
                                                                    'action'
                                                                }
                                                                onToggle={() =>
                                                                    setMenuActionId(
                                                                        menuOpen
                                                                            ? null
                                                                            : action.id
                                                                    )
                                                                }
                                                                showComplete={
                                                                    canComplete
                                                                }
                                                                onComplete={() => {
                                                                    setMenuActionId(
                                                                        null
                                                                    );
                                                                    requestComplete(
                                                                        action
                                                                    );
                                                                }}
                                                                onEdit={() => {
                                                                    setMenuActionId(
                                                                        null
                                                                    );
                                                                    setTextModal(
                                                                        {
                                                                            type: 'edit-action',
                                                                            title: 'Edit action',
                                                                            placeholder:
                                                                                'What will you do?',
                                                                            hint: action.done
                                                                                ? 'Completed action'
                                                                                : 'For today',
                                                                            initialValue:
                                                                                action.text ||
                                                                                '',
                                                                            action,
                                                                            dayOffset: 0,
                                                                        }
                                                                    );
                                                                }}
                                                                showRemove={
                                                                    canRemove
                                                                }
                                                                onRemove={() => {
                                                                    setMenuActionId(
                                                                        null
                                                                    );
                                                                    setConfirm({
                                                                        type: 'delete-action',
                                                                        id: action.id,
                                                                        title: 'Delete action?',
                                                                        desc: 'This action will be removed from your list.',
                                                                    });
                                                                }}
                                                            />
                                                        </li>
                                                    );
                                                }
                                            )}
                                        </ul>
                                    </div>
                                    <div className="dv2-at__tracker-list-footer">
                                        <button
                                            type="button"
                                            className="dv2-at__add-cta"
                                            onClick={openAddTodayAction}
                                            disabled={busy}
                                        >
                                            + Add more
                                        </button>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {tab === 'daily' ? (
                <div
                    className={`dv2-at__daily${
                        dayFetching ? ' is-day-fetching' : ''
                    }`}
                >
                    <div className="dv2-at__day-nav">
                        <button
                            type="button"
                            className="dv2-at__day-btn"
                            disabled={dayOffset <= 0}
                            onClick={() => setDayOffset((d) => Math.max(0, d - 1))}
                        >
                            ‹
                        </button>
                        <h2>{dayLabel(dayOffset)}</h2>
                        <button
                            type="button"
                            className="dv2-at__day-btn"
                            disabled={dayOffset >= 6}
                            onClick={() => setDayOffset((d) => Math.min(6, d + 1))}
                        >
                            ›
                        </button>
                    </div>

                    {listHint && dayOffset === 0 && namedPending.length > 0 ? (
                        <p className="dv2-at__hint">
                            Select an action below to mark it complete.
                        </p>
                    ) : null}

                    <div className="dv2-at__daily-scroll">
                        {loading && !actions.length ? (
                            <p className="dv2-at__muted">Loading actions…</p>
                        ) : (
                            <ul className="dv2-at__action-list">
                                {actions.map((action, index) => {
                                    const canClick =
                                        dayOffset === 0 &&
                                        !busy &&
                                        todayCount < MAX_DAILY &&
                                        action.canBeCompleted !== false;
                                    const canComplete = canClick;
                                    const canRemove =
                                        action.canBeRemoved !== false;
                                    const menuKey = `action-${action.id}`;
                                    const menuOpen = menuActionId === menuKey;
                                    const menuOpensUp =
                                        index >= actions.length - 2;
                                    return (
                                        <li
                                            key={action.id || `a-${index}`}
                                            className={[
                                                'dv2-at__action-row',
                                                canClick ? 'is-clickable' : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onClick={
                                                canClick
                                                    ? () =>
                                                          requestComplete(
                                                              action
                                                          )
                                                    : undefined
                                            }
                                            onKeyDown={
                                                canClick
                                                    ? (e) => {
                                                          if (
                                                              e.key ===
                                                                  'Enter' ||
                                                              e.key === ' '
                                                          ) {
                                                              e.preventDefault();
                                                              requestComplete(
                                                                  action
                                                              );
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            role={
                                                canClick ? 'button' : undefined
                                            }
                                            tabIndex={canClick ? 0 : undefined}
                                            aria-label={
                                                canClick
                                                    ? `Complete: ${action.text}`
                                                    : undefined
                                            }
                                        >
                                            <span
                                                className={`dv2-at__row-check${
                                                    !canClick
                                                        ? ' is-disabled'
                                                        : ''
                                                }`}
                                                aria-hidden
                                            />
                                            <span className="dv2-at__action-num">
                                                {String(index + 1).padStart(
                                                    2,
                                                    '0'
                                                )}
                                            </span>
                                            <span className="dv2-at__row-text">
                                                {action.text || 'Action step'}
                                            </span>
                                            <ActionStepMenu
                                                open={menuOpen}
                                                opensUp={menuOpensUp}
                                                menuRef={menuRef}
                                                label={
                                                    action.text || 'action'
                                                }
                                                onToggle={() =>
                                                    setMenuActionId(
                                                        menuOpen
                                                            ? null
                                                            : menuKey
                                                    )
                                                }
                                                showComplete={canComplete}
                                                onComplete={() => {
                                                    setMenuActionId(null);
                                                    requestComplete(action);
                                                }}
                                                onEdit={() => {
                                                    setMenuActionId(null);
                                                    setTextModal({
                                                        type: 'edit-action',
                                                        title: 'Edit action',
                                                        placeholder:
                                                            'What will you do?',
                                                        hint: dayLabel(
                                                            dayOffset
                                                        ),
                                                        initialValue:
                                                            action.text || '',
                                                        action,
                                                        dayOffset,
                                                    });
                                                }}
                                                showRemove={canRemove}
                                                onRemove={() => {
                                                    setMenuActionId(null);
                                                    setConfirm({
                                                        type: 'delete-action',
                                                        id: action.id,
                                                        title: 'Delete action?',
                                                        desc: 'This action will be removed from your list.',
                                                    });
                                                }}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}

                        {!loading && !dayFetching && actions.length === 0 ? (
                            <p className="dv2-at__muted">
                                No actions for this day yet. Add your first one.
                            </p>
                        ) : null}

                        {dayFetching && actions.length === 0 ? (
                            <p className="dv2-at__muted">Loading actions…</p>
                        ) : null}
                    </div>

                    <div className="dv2-at__daily-footer">
                        <button
                            type="button"
                            className="dv2-at__btn-ghost"
                            onClick={() =>
                                setTextModal({
                                    type: 'add-action',
                                    title: 'Add action',
                                    placeholder: 'What will you do?',
                                })
                            }
                        >
                            + Add more
                        </button>
                    </div>
                </div>
            ) : null}

            {tab === 'todo' ? (
                <div className="dv2-at__todo">
                    {todoLocked ? (
                        <p className="dv2-at__muted">
                            To-Do List is available with an active subscription.
                        </p>
                    ) : (
                        <>
                            <div className="dv2-at__day-nav">
                                <button
                                    type="button"
                                    className="dv2-at__day-btn"
                                    disabled={dayOffset <= 0}
                                    onClick={() =>
                                        setDayOffset((d) => Math.max(0, d - 1))
                                    }
                                >
                                    ‹
                                </button>
                                <h2>
                                    {currentTodoDay?.day ||
                                        dayLabel(dayOffset)}
                                </h2>
                                <button
                                    type="button"
                                    className="dv2-at__day-btn"
                                    disabled={dayOffset >= 6}
                                    onClick={() =>
                                        setDayOffset((d) => Math.min(6, d + 1))
                                    }
                                >
                                    ›
                                </button>
                            </div>

                            {currentTodoDay?.date ? (
                                <p className="dv2-at__todo-date">
                                    {currentTodoDay.date}
                                </p>
                            ) : null}

                            <div className="dv2-at__todo-scroll">
                                <ul className="dv2-at__todo-list">
                                    {currentTodoTasks.map((task, index) => {
                                        const canClick = !busy;
                                        const menuKey = `todo-${task.id}`;
                                        const menuOpen =
                                            menuActionId === menuKey;
                                        const menuOpensUp =
                                            index >=
                                            currentTodoTasks.length - 2;
                                        return (
                                            <li
                                                key={task.id}
                                                className={[
                                                    'dv2-at__todo-row',
                                                    canClick
                                                        ? 'is-clickable'
                                                        : '',
                                                    task.dateInPast
                                                        ? 'is-past'
                                                        : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                onClick={
                                                    canClick
                                                        ? () =>
                                                              requestCompleteTodo(
                                                                  task
                                                              )
                                                        : undefined
                                                }
                                                onKeyDown={
                                                    canClick
                                                        ? (e) => {
                                                              if (
                                                                  e.key ===
                                                                      'Enter' ||
                                                                  e.key === ' '
                                                              ) {
                                                                  e.preventDefault();
                                                                  requestCompleteTodo(
                                                                      task
                                                                  );
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                role={
                                                    canClick
                                                        ? 'button'
                                                        : undefined
                                                }
                                                tabIndex={
                                                    canClick ? 0 : undefined
                                                }
                                                aria-label={
                                                    canClick
                                                        ? `Complete: ${task.text}`
                                                        : undefined
                                                }
                                            >
                                                <span
                                                    className={`dv2-at__row-check${
                                                        !canClick
                                                            ? ' is-disabled'
                                                            : ''
                                                    }`}
                                                    aria-hidden
                                                />
                                                <div className="dv2-at__todo-main">
                                                    <span className="dv2-at__row-text">
                                                        {task.text || 'Task'}
                                                    </span>
                                                    {task.dateInPast ? (
                                                        <span className="dv2-at__todo-past-tag">
                                                            {task.dateInPast}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <ActionStepMenu
                                                    open={menuOpen}
                                                    opensUp={menuOpensUp}
                                                    menuRef={menuRef}
                                                    label={
                                                        task.text || 'task'
                                                    }
                                                    onToggle={() =>
                                                        setMenuActionId(
                                                            menuOpen
                                                                ? null
                                                                : menuKey
                                                        )
                                                    }
                                                    showComplete={canClick}
                                                    onComplete={() => {
                                                        setMenuActionId(null);
                                                        requestCompleteTodo(
                                                            task
                                                        );
                                                    }}
                                                    onEdit={() => {
                                                        setMenuActionId(null);
                                                        setTextModal({
                                                            type: 'edit-todo',
                                                            title: 'Edit task',
                                                            placeholder:
                                                                'Task name',
                                                            hint:
                                                                currentTodoDay?.day ||
                                                                dayLabel(
                                                                    dayOffset
                                                                ),
                                                            initialValue:
                                                                task.text ||
                                                                '',
                                                            task,
                                                        });
                                                    }}
                                                    onRemove={() => {
                                                        setMenuActionId(null);
                                                        setConfirm({
                                                            type: 'delete-todo',
                                                            id: task.id,
                                                            title: 'Delete task?',
                                                            desc: 'This task will be removed.',
                                                        });
                                                    }}
                                                />
                                            </li>
                                        );
                                    })}
                                </ul>

                                {!currentTodoTasks.length ? (
                                    <p className="dv2-at__muted">
                                        No tasks for this day yet. Add your
                                        first one.
                                    </p>
                                ) : null}
                            </div>

                            <div className="dv2-at__daily-footer">
                                <button
                                    type="button"
                                    className="dv2-at__btn-ghost"
                                    onClick={() =>
                                        setTextModal({
                                            type: 'add-todo',
                                            dayNumber:
                                                currentTodoDay?.dayNumber ??
                                                dayOffset,
                                            title: `Add task · ${
                                                currentTodoDay?.day ||
                                                dayLabel(dayOffset)
                                            }`,
                                            placeholder: 'Task name',
                                        })
                                    }
                                >
                                    + Add more
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : null}

            {confirm ? (
                <ConfirmModal
                    title={confirm.title}
                    desc={confirm.desc}
                    confirmLabel={
                        confirm.type?.includes('delete') ? 'Delete' : 'Complete'
                    }
                    busy={busy}
                    onCancel={() => setConfirm(null)}
                    onConfirm={() => {
                        if (confirm.type === 'empty') runEmptyComplete();
                        else if (confirm.type === 'complete')
                            runComplete(confirm.id);
                        else if (confirm.type === 'delete-action')
                            deleteAction(confirm.id);
                        else if (confirm.type === 'complete-todo')
                            completeTodo(confirm.id);
                        else if (confirm.type === 'delete-todo')
                            deleteTodo(confirm.id);
                    }}
                />
            ) : null}

            {textModal ? (
                <TextModal
                    key={`${textModal.type}-${
                        textModal.action?.id ||
                        textModal.task?.id ||
                        'new'
                    }`}
                    title={textModal.title}
                    placeholder={textModal.placeholder}
                    hint={textModal.hint || ''}
                    initialValue={textModal.initialValue || ''}
                    busy={busy}
                    wide={
                        textModal.type === 'add-action' ||
                        textModal.type === 'edit-action' ||
                        textModal.type === 'add-todo' ||
                        textModal.type === 'edit-todo'
                    }
                    onCancel={() => setTextModal(null)}
                    onSave={(value) => {
                        if (textModal.type === 'add-action')
                            saveActionText(
                                value,
                                null,
                                textModal.dayOffset != null
                                    ? textModal.dayOffset
                                    : null
                            );
                        else if (textModal.type === 'edit-action')
                            saveActionText(
                                value,
                                textModal.action,
                                textModal.dayOffset != null
                                    ? textModal.dayOffset
                                    : 0
                            );
                        else if (textModal.type === 'add-todo')
                            addTodo(value, textModal.dayNumber);
                        else if (textModal.type === 'edit-todo')
                            editTodo(value, textModal.task);
                    }}
                />
            ) : null}
        </section>
    );
};

export default ActionTrackerWorkspace;
